import React from "react"
import { Alert, Button, Card, Col, Space, Table, Tag, Typography } from "antd"
import { CheckOutlined, DownOutlined, PlusOutlined, ShoppingOutlined, UpOutlined } from "@ant-design/icons"
import { TMovementElementItem } from "../../../../models/salesModel"

const { Text } = Typography

interface NoteBillItemsCatalogProps {
  billItems?: TMovementElementItem[]
  noteItems: TMovementElementItem[]
  noteType: 'CREDIT_NOTE' | 'DEBIT_NOTE'
  isPhysical: boolean
  isAnnulment: boolean
  reason?: string
  itemRemainingQty: Record<number, number>
  isItemsCatalogCollapsed: boolean
  onToggleCollapse: () => void
  onAddItem: (item: TMovementElementItem) => void
  onAddCustomCharge: () => void
}

export const NoteBillItemsCatalog: React.FC<NoteBillItemsCatalogProps> = ({
  billItems,
  noteItems,
  noteType,
  isPhysical,
  isAnnulment,
  reason,
  itemRemainingQty,
  isItemsCatalogCollapsed,
  onToggleCollapse,
  onAddItem,
  onAddCustomCharge
}) => {
  if (isAnnulment) {
    return null
  }

  return (
    <Col xs={24} xl={8} xxl={7}>
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, width: '100%' }}>
            <Space align="center" size={6}>
              <ShoppingOutlined style={{ color: '#1890ff' }} />
              <span style={{ fontWeight: 600, fontSize: 13 }}>Ítems Factura</span>
              <Tag color="default" style={{ margin: 0 }}>{billItems?.length || 0}</Tag>
            </Space>
            <Space align="center" size={4} wrap>
              {(noteType === 'DEBIT_NOTE' || (noteType === 'CREDIT_NOTE' && !isPhysical)) && (
                <Button 
                  type="dashed" 
                  size="small" 
                  icon={<PlusOutlined />} 
                  onClick={onAddCustomCharge}
                  disabled={!reason}
                  style={{ fontSize: 12, padding: '0 8px' }}
                >
                  {noteType === 'DEBIT_NOTE' ? "Cargo" : "Descuento"}
                </Button>
              )}
              <Button 
                type="text" 
                size="small" 
                icon={isItemsCatalogCollapsed ? <DownOutlined /> : <UpOutlined />} 
                onClick={onToggleCollapse}
                title={isItemsCatalogCollapsed ? "Mostrar ítems" : "Ocultar ítems"}
                style={{ padding: '0 4px' }}
              />
            </Space>
          </div>
        } 
        className="card shadow-sm"
        size="small"
      >
        {!isItemsCatalogCollapsed && (
          <>
            {!reason && (
              <Alert 
                message="Seleccione un motivo en la configuración para agregar ítems." 
                type="warning" 
                showIcon 
                style={{ marginBottom: 12 }} 
              />
            )}
            <Table
              size="small"
              dataSource={billItems}
              rowKey="id"
              pagination={false}
              scroll={{ x: 'max-content', y: (billItems && billItems.length > 5) ? 220 : undefined }}
              columns={[
                { 
                  title: "Item", 
                  dataIndex: "name",
                  render: (text: string) => <Text strong style={{ fontSize: 13 }}>{text}</Text>
                },
                { 
                  title: "Cant.", 
                  dataIndex: "quantity", 
                  width: 55, 
                  align: "center" as const 
                },
                ...(noteType === 'CREDIT_NOTE' && isPhysical ? [{ 
                  title: "Disp.", 
                  render: (_: any, record: any) => {
                    const rem = itemRemainingQty[record.id] ?? 0
                    return <Tag color={rem > 0 ? "blue" : "default"}>{rem}</Tag>
                  },
                  width: 55,
                  align: "center" as const
                }] : []),
                { 
                  title: "Acción", 
                  width: 75, 
                  align: "center" as const,
                  render: (_: any, record: any) => {
                    const isAdded = noteItems.some(i => i.id === record.id)
                    const isExhausted = noteType === 'CREDIT_NOTE' && isPhysical && (itemRemainingQty[record.id] || 0) <= 0
                    return (
                      <Button 
                        type={isAdded ? "default" : "primary"} 
                        size="small" 
                        icon={isAdded ? <CheckOutlined /> : <PlusOutlined />} 
                        onClick={() => onAddItem(record)}
                        disabled={
                          isAnnulment ||
                          isAdded || 
                          !reason || 
                          isExhausted
                        }
                        style={{ padding: '0 8px' }}
                      >
                        {isAdded ? "Listo" : "Agregar"}
                      </Button>
                    )
                  }
                }
              ]}
            />
          </>
        )}
      </Card>
    </Col>
  )
}
